import { api } from "@/api";
import type { RefreshSuggestion } from "@/shared/blog/suggestions";
import type { ListSuggestionsParams, SuggestionsService } from "./suggestionsService.contract";

/**
 * Implementação HTTP da fila de sugestões (E7.3). Formato real do notifyme
 * (`ResponseBlogRefreshSuggestion`, snake_case): a listagem é um array puro,
 * não `{ items }` — ler `items` deixava a tela vazia com sugestões pendentes
 * no banco.
 */

const BACKOFFICE = "v1/backoffice/blog/refresh-suggestions";

interface ApiSuggestion {
  id: string;
  post_id: string;
  /** Ausente em versões do backend anteriores ao NotiifyMe#440. */
  post_title?: string;
  kind: RefreshSuggestion["kind"];
  rationale: string;
  /** jsonb livre no backend; o job atual nunca preenche. */
  proposed_links?: unknown[];
  priority: number;
  status: RefreshSuggestion["status"];
  dismiss_reason: string | null;
  created_at: string;
  updated_at: string;
}

const toLink = (link: unknown): string | null => {
  if (typeof link === "string") return link;
  if (link && typeof link === "object") {
    const { url, href, slug } = link as Record<string, unknown>;
    const value = url ?? href ?? slug;
    return typeof value === "string" ? value : null;
  }
  return null;
};

const mapSuggestion = (api: ApiSuggestion): RefreshSuggestion => {
  const proposedLinks = (api.proposed_links ?? [])
    .map(toLink)
    .filter((link): link is string => link !== null);

  return {
    id: api.id,
    postId: api.post_id,
    postTitle: api.post_title ?? "",
    kind: api.kind,
    rationale: api.rationale,
    proposedLinks: proposedLinks.length > 0 ? proposedLinks : undefined,
    priority: api.priority,
    status: api.status,
    createdAt: api.created_at,
    // O backend não tem resolved_at: a última atualização de uma sugestão
    // fora de "pendente" é a decisão.
    resolvedAt: api.status === "pendente" ? null : api.updated_at,
    resolutionReason: api.dismiss_reason,
  };
};

const listSuggestions = async (
  params: ListSuggestionsParams = {}
): Promise<RefreshSuggestion[]> => {
  const { data } = await api.get<ApiSuggestion[]>(BACKOFFICE, {
    params: {
      post_id: params.postId || undefined,
      status: params.status || undefined,
    },
  });
  return (data ?? []).map(mapSuggestion);
};

/** O backend devolve o post novo (`ResponseBlogPost`); o id dele é o rascunho. */
const applySuggestionAsDraft = async (id: string): Promise<{ draftPostId: string }> => {
  const { data } = await api.patch<{ id: string }>(`${BACKOFFICE}/${id}/apply-draft`);
  return { draftPostId: data.id };
};

const dismissSuggestion = async (id: string, reason: string): Promise<RefreshSuggestion> => {
  const { data } = await api.patch<ApiSuggestion>(`${BACKOFFICE}/${id}/dismiss`, {
    reason,
  });
  return mapSuggestion(data);
};

export const httpSuggestionsService: SuggestionsService = {
  listSuggestions,
  applySuggestionAsDraft,
  dismissSuggestion,
};
